import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Rocket, GraduationCap, BookOpen, WhatsappLogo, ArrowRight, Sparkle } from "@phosphor-icons/react";

const STORAGE_KEY = "sbl_flashcard_dismissed_v1";
// Replace this when SBL gets a permanent WhatsApp invite
const WHATSAPP_LINK = "https://chat.whatsapp.com/EPkjYMSbctX5n9wmtRutql";

const PATHS = [
  {
    icon: Rocket,
    accent: "from-blue-700 to-indigo-800",
    iconBg: "bg-blue-100 text-blue-800",
    title: "Enrolling Programme",
    blurb: "Apply for a flagship cohort and start your school-leadership journey now.",
    cta: "Apply Now",
    type: "link",
    to: "/register?mode=programme",
    testid: "flashcard-apply-now",
  },
  {
    icon: GraduationCap,
    accent: "from-emerald-700 to-teal-800",
    iconBg: "bg-emerald-100 text-emerald-800",
    title: "Explore Our Programmes",
    blurb: "Browse all 28 specialised SBL programmes — from foundations to fellowship.",
    cta: "Go to Programmes",
    type: "link",
    to: "/programmes-catalogue",
    testid: "flashcard-go-programmes",
  },
  {
    icon: BookOpen,
    accent: "from-amber-600 to-orange-700",
    iconBg: "bg-amber-100 text-amber-800",
    title: "Start a Free Course",
    blurb: "Sample our teaching style with handpicked free courses — no card needed.",
    cta: "View Free Courses",
    type: "link",
    to: "/free-courses",
    testid: "flashcard-free-courses",
  },
  {
    icon: WhatsappLogo,
    accent: "from-green-700 to-green-900",
    iconBg: "bg-green-100 text-green-800",
    title: "Join Our Learning Community",
    blurb: "Hop into our WhatsApp waitlist to get cohort updates, scholarships, and tips.",
    cta: "Join WhatsApp Waitlist",
    type: "external",
    to: WHATSAPP_LINK,
    testid: "flashcard-whatsapp",
  },
];

export default function LandingFlashCard() {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    // Skip the welcome modal when the visitor lands via #contact (e.g. clicked
    // "Make Enquiry" from a non-enrolling programme) — we don't want to cover
    // the form they were sent to.
    if (typeof window !== "undefined" && (window.location.hash || "").toLowerCase() === "#contact") {
      sessionStorage.setItem(STORAGE_KEY, "1");
      return;
    }
    // Defer slightly so the page paints first — pop-up feels welcoming, not jumpy
    const t = setTimeout(() => {
      setMounted(true);
      requestAnimationFrame(() => setOpen(true));
    }, 600);
    return () => clearTimeout(t);
  }, []);

  const handleClose = () => {
    setOpen(false);
    sessionStorage.setItem(STORAGE_KEY, "1");
  };

  if (!mounted) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent
        className="rounded-none sm:max-w-3xl md:max-w-4xl max-h-[92vh] overflow-y-auto p-0 border-0"
        data-testid="landing-flashcard"
      >
        {/* Top accent strip + close already provided by DialogContent */}
        <div className="bg-gradient-to-r from-slate-950 via-blue-950 to-slate-950 text-white px-6 py-5 sm:px-10 sm:py-7">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <Sparkle size={16} weight="fill" className="text-amber-300" />
              <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.25em] text-amber-300">
                Welcome to School Builder Lab Africa
              </p>
            </div>
            <DialogTitle
              className="text-2xl sm:text-3xl text-white"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Where would you like to start today?
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs sm:text-sm text-blue-100 mt-2 max-w-2xl">
            Pick the path that fits your goals — apply for a programme, browse the catalogue, sample a free course, or join our community.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-5 sm:p-7 bg-slate-50">
          {PATHS.map((p, idx) => {
            const Icon = p.icon;
            const inner = (
              <div className="bg-white border border-slate-200 hover:border-blue-700 hover:shadow-lg transition-all p-5 h-full flex flex-col group cursor-pointer rounded-none">
                <div className={`w-11 h-11 ${p.iconBg} rounded-none flex items-center justify-center mb-3`}>
                  <Icon size={22} weight="fill" />
                </div>
                <h3
                  className="text-base sm:text-lg font-bold text-slate-950 mb-1"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {p.title}
                </h3>
                <p className="text-xs text-slate-500 mb-4 flex-1">{p.blurb}</p>
                <Button
                  className={`bg-gradient-to-r ${p.accent} text-white hover:opacity-90 rounded-none w-full justify-between gap-2 group-hover:translate-x-0.5 transition-transform`}
                  data-testid={p.testid}
                >
                  <span className="text-sm">{p.cta}</span>
                  <ArrowRight size={14} weight="bold" />
                </Button>
              </div>
            );
            if (p.type === "external") {
              return (
                <a
                  key={idx}
                  href={p.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleClose}
                  className="block focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-700"
                >
                  {inner}
                </a>
              );
            }
            return (
              <Link key={idx} to={p.to} onClick={handleClose} className="block focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-700">
                {inner}
              </Link>
            );
          })}
        </div>

        <div className="bg-white px-5 sm:px-7 pb-6 text-center">
          <button
            onClick={handleClose}
            className="text-xs text-slate-500 hover:text-slate-900 underline underline-offset-2"
            data-testid="flashcard-skip"
          >
            Maybe later — let me browse first
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
